import _ from 'lodash';
import { pathToRegexp, compile } from 'path-to-regexp';
import queryString from 'query-string';
import { replace } from 'lodash';

/**
 * Populates a dynamic pathname with values
 * Example:
 * `buildPath('foo/:bar/:baz', { bar: 'potato', baz: 'tomato' })`
 * returns `foo/potato/tomato`
 *
 * See https://github.com/pillarjs/path-to-regexp#compile-reverse-path-to-regexp for more info
 *
 * @param {string} path - the base path (e.g. 'foo/:bar/:baz')
 * @param {object} params - an object of params to update
 * @returns {string} - the populated pathname
 */
export const buildPath = (path, params) => compile(path)(params);

/**
 * Given a target path and actual path, determines if the path matches
 * Example:
 * `doesPathMatch('foo/:bar/:baz', 'foo/potato/tomato')`
 * returns `true`
 *
 * Example:
 * `doesPathMatch('foo/:bar/:baz', 'idaho/iowa/illinois')`
 * returns `false`
 *
 * @param {string} targetPath - The path to test against (e.g. 'foo/:bar/:baz')
 * @param {string} myPath - The path to test (e.g. 'foo/potato/tomato')
 * @param {object} [options] - See https://github.com/pillarjs/path-to-regexp#usage for option info
 * @returns {boolean} - Whether the path matches or not
 */
export const doesPathMatch = (targetPath, myPath, options) =>
  !!pathToRegexp(targetPath, undefined, options).exec(myPath);

/**
 * Given a target path and actual path, returns all parameters by name
 * Example:
 * `getParamsFromPath('foo/:bar/:baz', 'foo/potato/tomato')
 * returns `{ bar: 'potato', baz: 'tomato' }`
 *
 * @param {string} targetPath - The path to test against (e.g. 'foo/:bar/:baz')
 * @param {string} myPath - The path to test (e.g. 'foo/potato/tomato')
 * @param {object} [options] - See https://github.com/pillarjs/path-to-regexp#usage for option info
 * @returns {object} - map of parameterName:value
 */
export const getParamsFromPath = (targetPath, myPath, options) => {
  const keys = [];
  const re = pathToRegexp(targetPath, keys, options);
  const result = re.exec(myPath);

  if (!result) {
    return {};
  }

  const params = {};
  // skip the first result, which is the full path
  // eslint-disable-next-line no-plusplus
  for (let i = 1; i < result.length; i++) {
    const key = keys[i - 1].name;
    params[key] = result[i];
  }
  return params;
};

const isNerdWalletLink = (link) =>
  _.startsWith(link, process.env.NEXT_PUBLIC_INTERNAL_BASE_URL);

/**
 * Convenience method for creating a URL with query params to convey
 * navigation_source_impression_id
 * @param {string} basePath - URL or relative path
 * @param {string} impressionId
 *
 * @return {string} Absolute or relative URL with origin_impression_id query param
 */
export const getUrlPathWithTracking = (basePath, impressionId) => {
  if (!basePath) return '';

  // If `url` is a relative URL string, then use SITE_BASE_URL to create an absolute URL.
  const url = new URL(basePath, process.env.NEXT_PUBLIC_INTERNAL_BASE_URL);
  const params = queryString.parse(url.search);
  url.search = queryString.stringify({
    ...params,
    origin_impression_id: impressionId,
  });

  // If URL is an internal link (= matches SITE_BASE_URL), return a relative URL instead of absolute
  if (isNerdWalletLink(url.toString())) {
    return replace(url, process.env.NEXT_PUBLIC_INTERNAL_BASE_URL, '');
  }

  return url.toString();
};

/**
 * Convenience method for `user_navigated` that adds `origin_impression_id` to a location object.
 * Refinr copies this value to `navigation_source_impression_id`.
 *
 * @param {object|string} to - Location object or string pathname
 * @param {string} impressionId
 *
 * @return {string|object} Location object or URL with `origin_impression_id` in the query string
 */
export const getRouterPathWithTracking = (to, impressionId) => {
  if (!to || !impressionId) return to;
  if (typeof to === 'string') return getUrlPathWithTracking(to, impressionId);

  const qp = queryString.parse(to?.search);
  return {
    ...to,
    search: `?${queryString.stringify({
      ...qp,
      origin_impression_id: impressionId,
    })}`,
  };
};
